import { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { Modal } from '../../components/Modal';
import { api } from '../../lib/api';
import type { LinhaReposicao } from './Reposicoes';

// O painel que RESOLVE a fila. Vive fora da tela de propósito: ele é o mesmo nas duas vistas
// (Mobile e Desktop). A vista muda a FORMA de ler a lista, nunca o que se resolve dali — foi
// exatamente esse o tropeço da vista lado a lado da cotação ([DA-348]): ela nasceu só-leitura, a
// preferência ficou salva, a tela passou a abrir nela e o botão que enchia a coluna estava na
// outra. Vista nova sem a ferramenta junto vira beco.
interface Movimento {
  id: number;
  quantidade: string;
  criado_em: string;
  origem_codigo: string | null;
  destino_codigo: string | null;
  usuario_login: string | null;
}

const num = (v: unknown) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const fmt = (v: unknown) => String(num(v)).replace('.', ',');

/** Carimbo vem em `timestamptz` — o helper da casa trata as duas grafias que a API devolve. */
function fmtQuando(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

interface Props {
  linha: LinhaReposicao | null;
  onFechar: () => void;
  onPronto: () => void;
}

export function PainelTransferir({ linha, onFechar, onPronto }: Props) {
  const [origemId, setOrigemId] = useState<number>(0);
  const [quantidade, setQuantidade] = useState<string>('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [historico, setHistorico] = useState<Movimento[]>([]);

  // Abrir um produto novo reinicia tudo: a origem mais cheia já vem escolhida e a quantidade
  // sugerida já vem preenchida. Quem está de pé na frente da prateleira confirma, não calcula.
  useEffect(() => {
    if (!linha) return;
    setOrigemId(linha.origens[0]?.localizacao_id ?? 0);
    setQuantidade(String(num(linha.sugerido)).replace('.', ','));
    setErro(null);
    setHistorico([]);
    api.get<Movimento[]>(`/api/reposicoes/movimentos?produto_id=${linha.produto_id}`)
      .then(setHistorico)
      .catch(() => { /* histórico é contexto, não pode impedir a transferência */ });
  }, [linha]);

  if (!linha) return null;

  const origem = linha.origens.find((o) => o.localizacao_id === origemId) ?? null;
  const qtd = num(quantidade.replace(',', '.'));
  const temNaOrigem = origem ? num(origem.quantidade) : 0;
  // Trava espelhando o backend. A do front existe pra o operador ver ANTES de clicar — nunca pra
  // ser mais rígida que o servidor, que é quem garante de verdade (com as duas linhas travadas).
  const excedeu = qtd > temNaOrigem;
  const invalido = !origem || qtd <= 0 || excedeu || salvando;

  async function transferir() {
    if (!linha || !origem) return;
    setSalvando(true);
    setErro(null);
    try {
      await api.post('/api/reposicoes/transferir', {
        produto_id: linha.produto_id,
        origem_id: origem.localizacao_id,
        destino_id: linha.vitrine_id,
        quantidade: qtd,
      });
      onPronto();
    } catch (e: any) {
      setErro(e?.corpo?.erro ?? e?.message ?? 'Nao foi possivel transferir.');
      setSalvando(false);
    }
  }

  return (
    <Modal
      aberto
      onFechar={onFechar}
      titulo={`Repor ${linha.codigo_interno ?? ''} — ${linha.descricao}`}
      largura={560}
      rodape={
        <>
          <button type="button" className="btn-secundario" onClick={onFechar} disabled={salvando}>Cancelar</button>
          <button type="button" className="btn-primario" onClick={transferir} disabled={invalido}>
            {salvando ? 'Transferindo...' : 'Confirmar transferencia'}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* O de-para em uma linha: de onde sai, pra onde vai. É o desenho do movimento físico que a
            pessoa vai fazer com as mãos — por isso a seta, e não dois campos soltos. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 180px' }}>
            <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }} className="muted">Tirar de</label>
            <select value={origemId} onChange={(e) => setOrigemId(Number(e.target.value))} style={{ width: '100%' }}>
              {linha.origens.map((o) => (
                <option key={o.localizacao_id} value={o.localizacao_id}>
                  {o.codigo} — tem {fmt(o.quantidade)}
                </option>
              ))}
            </select>
          </div>
          <ArrowRight size={18} style={{ marginTop: 18, flex: '0 0 auto' }} />
          <div style={{ flex: '1 1 180px' }}>
            <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }} className="muted">Levar pra vitrine</label>
            <div
              style={{
                padding: '8px 14px',
                border: '1px solid var(--input-border)',
                borderRadius: 4,
                background: 'var(--input-bg)',
                color: 'var(--input-fg)',
                fontWeight: 600,
              }}
            >
              {linha.vitrine_codigo} — tem {fmt(linha.vitrine_qtd)}
            </div>
          </div>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }} className="muted">Quantidade</label>
          <input
            value={quantidade}
            onChange={(e) => setQuantidade(e.target.value)}
            inputMode="decimal"
            style={{ width: 160, fontSize: 18, fontWeight: 700 }}
            autoFocus
          />
          <span className="muted" style={{ marginLeft: 10, fontSize: 13 }}>
            {linha.unidade_sigla ?? 'un'} — sugerido {fmt(linha.sugerido)} (o minimo da vitrine)
          </span>
          {excedeu && (
            <p style={{ background: '#f97316', color: '#000', padding: '6px 10px', borderRadius: 3, marginTop: 8, fontSize: 13 }}>
              {origem?.codigo} tem {fmt(temNaOrigem)} — nao da pra tirar {fmt(qtd)}.
            </p>
          )}
        </div>

        {/* O total NÃO muda: a mercadoria continua na loja, só troca de prateleira. Dizer isso na
            tela evita a pergunta óbvia ("isso vai mexer no meu estoque?") e explica por que a
            transferência não passa pela cascata. */}
        <p className="muted" style={{ fontSize: 12, margin: 0 }}>
          O estoque total do produto ({fmt(linha.estoque_atual)}) <strong>nao muda</strong> — a mercadoria
          so troca de lugar dentro da loja.
        </p>

        {erro && (
          <p style={{ background: '#f97316', color: '#000', padding: '8px 12px', borderRadius: 3, fontSize: 13 }}>{erro}</p>
        )}

        {historico.length > 0 && (
          <div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Ultimas reposicoes deste produto</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {historico.map((m) => (
                <div key={m.id} className="muted" style={{ fontSize: 12 }}>
                  {fmtQuando(m.criado_em)} · {fmt(m.quantidade)} {m.origem_codigo ?? '?'} → {m.destino_codigo ?? '?'}
                  {m.usuario_login ? ` · ${m.usuario_login}` : ''}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
